import React, { useEffect, useMemo, useRef, useState } from 'react';
/**
 * PhoneNumberField
 * ----------------
 * What:
 *   Country dial code dropdown (+44, +353, …) + free‑form phone input.
 *   Provides gentle sanitization and on‑blur normalization to E.164.
 *
 * Why:
 *   - Users arrive with varied formatting habits. We accept flexible input
 *     (digits, spaces, parentheses, hyphens, plus) and quietly normalize.
 *   - We show a minimal hint (“Looks valid”) for feedback but avoid blocking.
 *   - The selected country code is persisted in localStorage for convenience.
 *
 * Data model:
 *   - rawValue: what the user typed (kept for UX).
 *   - onNormalizedChange: emits E.164ish string on blur (server stores this).
 *   - onMetaChange: emits { countryCode, dialCode, trunkPrefix } for confirm.
 *
 * Implementation notes:
 *   - Normalization is simple: strip formatting, ensure a leading +dialCode,
 *     and optionally convert a national trunk prefix (e.g. leading 0 in GB).
 *   - We update hints only on blur to reduce flicker while typing.
 */
import { SUPPORTED_COUNTRIES, DEFAULT_COUNTRY_CODE } from '../constants/phoneCountries';

function digitsOnly(s) {
  return (s || '').replace(/\D/g, '');
}

function deriveE164(rawInput, dialCode, trunkPrefix) {
  const raw = String(rawInput || '');
  const cleaned = raw.replace(/[()\s-]/g, '');
  if (!dialCode) return '';
  const dial = String(dialCode);
  const dialDigits = digitsOnly(dial);
  if (cleaned.startsWith('+')) {
    return '+' + digitsOnly(cleaned);
  }
  const num = digitsOnly(cleaned);
  if (!num) return '';
  if (num.startsWith(dialDigits)) {
    return '+' + num;
  }
  if (trunkPrefix && num.startsWith(trunkPrefix)) {
    return dial + num.slice(trunkPrefix.length);
  }
  return dial + num;
}

export default function PhoneNumberField({
  rawValue,
  onRawChange,
  onNormalizedChange,
  onMetaChange,
  defaultCountryCode,
  label = 'Phone number',
  required = false
}) {
  const storageKey = 'rms_phone_country';
  const initialCode = useMemo(() => {
    const fromStorage = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
    return fromStorage || defaultCountryCode || DEFAULT_COUNTRY_CODE;
  }, [defaultCountryCode]);

  const [countryCode, setCountryCode] = useState(initialCode);
  const country = useMemo(() => SUPPORTED_COUNTRIES.find(c => c.code === countryCode) || SUPPORTED_COUNTRIES[0], [countryCode]);
  const [hint, setHint] = useState('');
  const [showHint, setShowHint] = useState(false);
  const liveRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(storageKey, countryCode); } catch (_) {}
    }
    if (onMetaChange) onMetaChange({ countryCode: country.code, dialCode: country.dialCode, trunkPrefix: country.trunkPrefix });
  }, [countryCode, country, onMetaChange]);

  const handleCountryChange = (e) => {
    setCountryCode(e.target.value);
  };

  const handleRawChange = (e) => {
    const val = e.target.value;
    // allow only digits, spaces, parentheses, hyphens, plus
    const cleaned = val.replace(/[^0-9()\s+-]/g, '');
    onRawChange?.(cleaned);
    // Recompute normalized value on every change so parent validation can react immediately
    const e164Live = deriveE164(cleaned, country.dialCode, country.trunkPrefix);
    const len = digitsOnly(e164Live).length;
    const ok = len >= 10 && len <= 20;
    if (ok) {
      onNormalizedChange?.(e164Live);
      // Clear any pending hint while typing valid number
      setHint('');
      setShowHint(false);
      if (liveRef.current) liveRef.current.textContent = '';
    } else {
      // If becomes invalid while typing, emit empty so validators fail until valid again
      onNormalizedChange?.('');
    }
  };

  const handleBlur = () => {
    const e164 = deriveE164(rawValue, country.dialCode, country.trunkPrefix);
    onNormalizedChange?.(e164);
    const len = digitsOnly(e164).length;
    const ok = len >= 10 && len <= 20;
    setHint(ok ? '' : 'Please enter a valid phone number.');
    setShowHint(!!rawValue && !ok);
    if (liveRef.current) liveRef.current.textContent = ok ? '' : 'Please enter a valid phone number.';
  };

  return (
    <div className="m-1 mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required ? <span className="text-red-600"> *</span> : null}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative shrink-0">
          <select
            value={countryCode}
            onChange={handleCountryChange}
            className="appearance-none pr-8 p-3 border-2 border-gray-200 rounded-lg bg-white text-gray-800 text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            aria-label="Country code"
          >
            {SUPPORTED_COUNTRIES.map(c => (
              <option key={c.code} value={c.code}>{c.dialCode}</option>
            ))}
          </select>
          <svg
            viewBox="0 0 20 20"
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
            aria-hidden="true"
          >
            <path d="M5.5 7.5l4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <input
          type="tel"
          inputMode="tel"
          value={rawValue || ''}
          onChange={handleRawChange}
          onBlur={handleBlur}
          placeholder="e.g. 07123 456 789"
          className="flex-1 min-w-0 p-3 border-2 border-gray-200 rounded-lg bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={liveRef} />
      <div className={`transition-all duration-300 overflow-hidden ${showHint ? 'max-h-6 mt-1' : 'max-h-0 mt-0'}`}>
        <div className={`text-xs text-red-600 transition-opacity duration-300 ${showHint ? 'opacity-100' : 'opacity-0'}`}>
          {hint}
        </div>
      </div>
    </div>
  );
}

export { deriveE164 };


